import './index.scss';
import React, { FocusEventHandler } from 'react';
import { CheckmarkIcon } from '../../assets/images/icons';
import { motion } from 'framer-motion';
import ParentAppService from '../../services/parentApp';
import { useTextareaFocused } from '../../hooks/use-textarea-focused';

export type InputPropsError = string[];

export interface TextareaProps {
  defaultValue?: string;
  placeholder?: string;
  className?: string;
  name?: string;
  For?: string;
  label?: string;
  errors?: InputPropsError;
  valid?: boolean;
  onChange?: (value: string) => void;
  style?: React.CSSProperties;
  motionDelay?: number;
  motionDuration?: number;
  autoComplete?: string;
  onBlur?: FocusEventHandler<HTMLTextAreaElement>;
  maxLength?: number;
  variant?: 'default' | 'large';
  tooltip?: string;
  defaultFocused?: boolean;
  onKeyUp?: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;
}

function Textarea({
  defaultValue = '',
  placeholder,
  label,
  className = '',
  name = ParentAppService.generateUUID(),
  errors = [],
  valid = false,
  onChange,
  style,
  motionDelay = 0,
  motionDuration = 0.45,
  autoComplete,
  onBlur,
  maxLength = 100,
  variant = 'default',
  tooltip,
  defaultFocused = false,
  onKeyUp,
}: TextareaProps) {
  const isValid = valid && !errors.length;

  const inputRef =
    React.useRef() as React.MutableRefObject<HTMLTextAreaElement>;
  const focused = useTextareaFocused(inputRef);

  const [value, setValue] = React.useState(defaultValue);

  React.useEffect(() => {
    if (defaultFocused && inputRef.current) {
      setTimeout(() => {
        if (inputRef.current?.focus) {
          inputRef.current.focus();
        }
      }, motionDelay + motionDuration * 1000);
    }
  }, []);

  return (
    <motion.div
      className={`textarea-component-container ${className} ${
        focused ? 'focused' : ''
      }`}
      style={style}
      initial={{ opacity: 0, translateY: '30px' }}
      animate={{ opacity: 1, translateY: '0%' }}
      exit={{ opacity: 0, transition: { delay: 0 } }}
      transition={{
        duration: motionDuration,
        delay: motionDelay,
        ease: 'anticipate',
      }}
    >
      <div
        className={`textarea-component-shadow ${variant}`}
        onClick={() => {
          if (inputRef.current?.focus) {
            inputRef.current.focus();
          }
        }}
      />
      <textarea
        onBlur={onBlur}
        onKeyUp={onKeyUp}
        ref={inputRef}
        data-toggle="tooltip"
        data-placement="top"
        title="This is the text of the tooltip"
        value={value}
        placeholder={placeholder}
        onChange={(event) => {
          setValue(event.target.value);
          if (onChange) {
            onChange(event.target.value);
          }
        }}
        autoComplete={autoComplete}
        className={`textarea-component-textarea ${
          errors.length ? 'error' : ''
        } ${variant}`}
        name={name}
        id={name}
        maxLength={maxLength}
      />
      {label ? (
        <label
          className={`textarea-component-label ${focused ? 'focused' : ''} ${
            value.length ? 'has-value' : ''
          } ${variant}`}
          htmlFor={name}
        >
          {label}
        </label>
      ) : null}
      <span
        className={`textarea-component-valid ${
          isValid ? 'active' : 'inactive'
        }`}
      >
        <CheckmarkIcon />
      </span>
    </motion.div>
  );
}

export default Textarea;
